import invariant from 'tiny-invariant'
import warning from 'tiny-warning'
import JSBI from 'jsbi'
import IPair from '../abis/IPair.json'
import IPythPriceFeed from '../abis/IPythPriceFeed.json'
import { getAddress } from '@ethersproject/address'
import IFactoryV2 from '../abis/IBrownFiV2Factory.json'
import IPythUpgradable from '../abis/IPythUpgradable.json'
import ethers from 'ethers'

import {
  BigintIsh,
  ZERO,
  ONE,
  TWO,
  THREE,
  SolidityType,
  SOLIDITY_TYPE_MAXIMA,
  ChainId,
  ROUTER_ADDRESS,
  ROUTER_ADDRESS_V1,
  ROUTER_ADDRESS_WITH_PRICE,
  FACTORY_ADDRESS,
  FACTORY_ADDRESS_V1,
  INIT_CODE_HASH,
  INIT_CODE_HASH_V1
} from './constants'
import Web3 from 'web3'
import { PYTH_ADDRESS, RPC_URLS } from '../constants/constants'
import axios, { AxiosResponse } from 'axios'
import { Pair } from './entities'

export function validateSolidityTypeInstance(
  value: JSBI,
  solidityType: SolidityType
): void {
  invariant(
    JSBI.greaterThanOrEqual(value, ZERO),
    `${value} is not a ${solidityType}.`
  )
  invariant(
    JSBI.lessThanOrEqual(value, SOLIDITY_TYPE_MAXIMA[solidityType]),
    `${value} is not a ${solidityType}.`
  )
}

// warns if addresses are not checksummed
export function validateAndParseAddress(
  address: string,
  chainId: ChainId
): string {
  try {
    if (chainId === ChainId.SN_MAIN || chainId === ChainId.SN_SEPOLIA) {
      return address
    }
    const checksummedAddress = getAddress(address)
    warning(address === checksummedAddress, `${address} is not checksummed.`)
    return checksummedAddress
  } catch (error) {
    invariant(false, `${address} is not a valid address.`)
  }
}

export function parseBigintIsh(bigintIsh: BigintIsh): JSBI {
  return bigintIsh instanceof JSBI
    ? bigintIsh
    : typeof bigintIsh === 'bigint'
    ? JSBI.BigInt(bigintIsh.toString())
    : JSBI.BigInt(bigintIsh)
}

// mock the on-chain sqrt function
export function sqrt(y: JSBI): JSBI {
  validateSolidityTypeInstance(y, SolidityType.uint256)
  let z: JSBI = ZERO
  let x: JSBI
  if (JSBI.greaterThan(y, THREE)) {
    z = y
    x = JSBI.add(JSBI.divide(y, TWO), ONE)
    while (JSBI.lessThan(x, z)) {
      z = x
      x = JSBI.divide(JSBI.add(JSBI.divide(y, x), x), TWO)
    }
  } else if (JSBI.notEqual(y, ZERO)) {
    z = ONE
  }
  return z
}

// given an array of items sorted by `comparator`, insert an item into its sort index and constrain the size to
// `maxSize` by removing the last item
export function sortedInsert<T>(
  items: T[],
  add: T,
  maxSize: number,
  comparator: (a: T, b: T) => number
): T | null {
  invariant(maxSize > 0, 'MAX_SIZE_ZERO')
  // this is an invariant because the interface cannot return multiple removed items if items.length exceeds maxSize
  invariant(items.length <= maxSize, 'ITEMS_SIZE')

  // short circuit first item add
  if (items.length === 0) {
    items.push(add)
    return null
  } else {
    const isFull = items.length === maxSize
    // short circuit if full and the additional item does not come before the last item
    if (isFull && comparator(items[items.length - 1], add) <= 0) {
      return add
    }

    let lo = 0
    let hi = items.length

    while (lo < hi) {
      const mid = (lo + hi) >>> 1
      if (comparator(items[mid], add) <= 0) {
        lo = mid + 1
      } else {
        hi = mid
      }
    }
    items.splice(lo, 0, add)
    return isFull ? items.pop()! : null
  }
}

export function isContractWithPrice(chainId: ChainId, version: number) {
  return version === 1 && !!ROUTER_ADDRESS_WITH_PRICE[chainId]
}

export function getRouterAddress(chainId: number, version: number) {
  return version === 2 ? ROUTER_ADDRESS[chainId] : ROUTER_ADDRESS_V1[chainId]
}

export function getFactoryAddress(chainId: number, version: number) {
  return version === 2 ? FACTORY_ADDRESS[chainId] : FACTORY_ADDRESS_V1[chainId]
}

export function getInitCodeHash(chainId: number, version: number) {
  return version === 2 ? INIT_CODE_HASH[chainId] : INIT_CODE_HASH_V1[chainId]
}

/**
 * Use when AddLiquidity v2 - Arbitrum Sepolia, Arbitrum One
 */
export const getPythPrice = async (
  address: string,
  chainId: ChainId,
  version: number
) => {
  if (!address || !chainId) return 0

  try {
    const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

    const factoryContract = new web3.eth.Contract(
      IFactoryV2 as any,
      getFactoryAddress(chainId, version)
    )

    const pythContract = new web3.eth.Contract(
      IPythUpgradable as any,
      PYTH_ADDRESS[chainId]
    )

    const priceFeedId = await factoryContract.methods
      .priceFeedIds(address)
      .call()

    const priceUnsafe = await pythContract.methods
      .getPriceUnsafe(priceFeedId)
      .call()

    return getPriceFromUnsafe(priceUnsafe)
  } catch (error) {
    console.warn('Cannot get Pyth price', error)
    return 0
  }
}

export const getPythPricePair = async (pair?: Pair, chainId?: ChainId) => {
  if (!pair || !chainId) return [0, 0]

  try {
    const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

    const pairContract = new web3.eth.Contract(
      IPair as any,
      pair.liquidityToken.address
    )
    const [priceFeedAddress, qti] = await Promise.all([
      pairContract.methods.priceFeed().call(),
      pairContract.methods.qti().call()
    ])

    const pythPriceFeedContract = new web3.eth.Contract(
      IPythPriceFeed as any,
      priceFeedAddress
    )

    const priceFeedIds: [string, string] = await Promise.all([
      pythPriceFeedContract.methods.baseTokenPriceId().call(),
      pythPriceFeedContract.methods.quoteTokenPriceId().call()
    ])

    const pythContract = new web3.eth.Contract(
      IPythUpgradable as any,
      PYTH_ADDRESS[chainId]
    )

    const priceUnsafes = await Promise.all([
      pythContract.methods.getPriceUnsafe(priceFeedIds[0]).call(),
      pythContract.methods.getPriceUnsafe(priceFeedIds[1]).call()
    ])

    if (+qti === 0) {
      return priceUnsafes.reverse().map(getPriceFromUnsafe)
    }
    return priceUnsafes.map(getPriceFromUnsafe)
  } catch (error) {
    console.warn('Cannot get Pyth price', error)
    return [0, 0]
  }
}

export const getPriceFromUnsafe = (priceUnsafe: string[]) => {
  const [price, conf, expo, publishTime] = priceUnsafe
  return +price * 10 ** +expo
}

export const getDataBytes = async (
  addresses: Array<string | undefined>,
  chainId: ChainId
) => {
  const web3 = new Web3(new Web3.providers.HttpProvider(RPC_URLS[chainId]))

  const factoryContract = new web3.eth.Contract(
    IFactoryV2 as any,
    getFactoryAddress(chainId, 2)
  )

  const priceFeedIds = await Promise.all(
    addresses.map((address) =>
      factoryContract.methods.priceFeedIds(address).call()
    )
  )

  const { data: dataBytes }: AxiosResponse<{ binary: { data: string[] } }> =
    await axios.get('https://hermes.pyth.network/v2/updates/price/latest', {
      params: {
        ids: priceFeedIds
      }
    })

  console.log('========', Date.now())
  console.log('dataBytes', dataBytes)

  return dataBytes.binary.data.map((byte) => `0x${byte}`)
}

export const solidityPack = async (
  addresses: Array<string | undefined>,
  chainId: ChainId
) => {
  const dataBytes = await getDataBytes(addresses, chainId)
  return ethers.utils.defaultAbiCoder.encode(['bytes[]'], [dataBytes])
}
